import { Toast } from "@aurem/ui";
import { useNavigation } from "hooks/navigate";
import { useI18n } from "providers/I18nProvider";
import { usePreferredLanguage } from "providers/PreferredLanguageProvider";
import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";

export function useLanguageSwitcher() {
  const navigate = useNavigation();
  const location = useLocation();
  const { updatePreferredLanguage } = usePreferredLanguage();
  const { language, languages, languageKeys, setLanguage } = useI18n();

  const languageOptions = useMemo(() => {
    return languageKeys.map((code) => ({
      code,
      label: languages[code],
    }));
  }, [languageKeys, languages]);

  const handleLanguageChange = useCallback(
    async (newLang: string) => {
      try {
        await updatePreferredLanguage(newLang);
        setLanguage(newLang as keyof typeof languages);
      } catch (error) {
        Toast.error({
          title: (error as Error).message,
        });
        return;
      }

      const currentPath = location.pathname.replace(
        new RegExp(`^/(${Object.keys(languages).join("|")})`),
        "",
      );
      const newUrl = `/${newLang}${currentPath}`;
      navigate(newUrl, { preserveSearchParams: true });
    },
    [
      location.pathname,
      updatePreferredLanguage,
      navigate,
      setLanguage,
      languages,
    ],
  );

  return {
    currentLanguage: language,
    languageOptions,
    handleLanguageChange,
  };
}
